import { Box } from '@mui/material';
import { DocumentStatus, DocumentType } from '@synergy/models';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { ReactElement } from 'react';
import { getUrlSegment } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';

import { ActivityObjectClass } from '../../../common/models/activity';
import { TransitionSource } from '../models/type-transition-source';
import { TransitionTarget } from '../models/internal/transition-target';
import { getDocumentTypeFromSource } from '../util/get-document-type-from-source';
import { useAppSelector } from '../../../store/use-appstate-selector';

interface Props {
    source: TransitionSource;
    target: TransitionTarget;
}

/**
 * Until #2319 offers only what is left, the dialog proposes the whole order
 * again. Where invoices already came from this order, this names them (R-258).
 */
export const ConvertReceiptAlreadyInvoiced = ({ source, target }: Props): ReactElement | null => {
    const activities = useAppSelector(state => state.transitionActivities.flat);

    if (DocumentType.PurchaseOrder !== getDocumentTypeFromSource(source)) {
        return null;
    }

    if (TransitionTarget.Invoice !== target && TransitionTarget.Credit !== target) {
        return null;
    }

    const invoices = (activities ?? []).filter(
        activity =>
            ActivityObjectClass.PurchaseOrder === activity.fromType &&
            source.id === activity.fromId &&
            ActivityObjectClass.Invoice === activity.toType &&
            // A cancelled invoice no longer bills anything from the order.
            DocumentStatus.Canceled !== activity.toStatus
    );

    if (0 === invoices.length) {
        return null;
    }

    return (
        <Box
            role="alert"
            sx={{
                mx: 3,
                mb: 2,
                p: '10px 14px',
                background: tokens.color.warnSoft,
                borderLeft: `4px solid ${tokens.color.warnInk}`,
                color: tokens.color.ink,
                fontSize: '13.5px',
                '& a': { color: tokens.color.warnInk, fontWeight: 600 },
            }}
        >
            <Box component="strong" sx={{ color: tokens.color.warnInk }}>
                !{' '}
                <FormattedMessage
                    id={
                        TransitionTarget.Credit === target
                            ? 'app.convertReceipt.alreadyInvoiced.credit'
                            : 'app.convertReceipt.alreadyInvoiced.title'
                    }
                />
            </Box>{' '}
            {invoices.map((invoice, index) => (
                <span key={invoice.id}>
                    {index > 0 && ', '}
                    <a
                        href={`/${getUrlSegment(DocumentType.Invoice)}/${invoice.toId}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {invoice.toNumber ?? <FormattedMessage id="app.convertReceipt.draft" />}
                    </a>
                    {undefined !== invoice.toTotalNetPrice && (
                        <>
                            {' ('}
                            <FormattedNumber value={invoice.toTotalNetPrice} style="currency" currency="EUR" />{' '}
                            <FormattedMessage id="app.convertReceipt.alreadyInvoiced.net" />)
                        </>
                    )}
                </span>
            ))}
            . <FormattedMessage id="app.convertReceipt.alreadyInvoiced.hint" />
        </Box>
    );
};
